/* Copyright (c) 2020-present CloudIO Inc */

import { atom, useStateXSetter } from '@cloudio-saas/statex';
import useDebounce from 'hooks/useDebounce';
import useIOActive from 'hooks/useIOActive';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { PubSub } from './PubSub';
import useEventListener from './useEventListener';

const getState = (): State => {
  if (document.visibilityState === 'hidden') {
    return 'hidden';
  }
  if (document.hasFocus()) {
    return 'active';
  }
  return 'passive';
};

export type State = 'active' | 'passive' | 'hidden';
type AppState = 'active' | 'passive';

interface EventMap {
  StateChange: {
    currentState: State;
    nextState: State;
    appState: AppState;
  };
  AppStateChange: {
    browserState: State;
    currentState: AppState;
    nextState: AppState;
  };
  Activity: { type: 'Request' | 'Trigger' };
}

export const ActivePubSub = new PubSub<EventMap>();

export const activesAtom = atom<AppState | null>({
  path: ['actives', ':userName'],
  defaultValue: null,
});

export const Actives = memo(() => {
  const set = useStateXSetter();
  useIOActive('cloudio', 'Active', 'Active', undefined, async (event) => {
    if (event.type === 'Touch' && event.touchData.$status) {
      set(activesAtom, event.touchData.$status, {
        params: { userName: event.active.createdBy },
      });
    } else if (event.type === 'Enter') {
      set(activesAtom, 'active', {
        params: { userName: event.active.createdBy },
      });
    } else if (event.type === 'Exit') {
      set(activesAtom, null, {
        params: { userName: event.active.createdBy },
      });
    }
  });

  return null;
});

function Active() {
  const stateRef = useRef<State>(getState());
  const appStateRef = useRef<AppState>('active');

  const logAppStateChange = useCallback(async (nextState: AppState) => {
    const prevState = appStateRef.current;
    if (nextState !== prevState) {
      await ActivePubSub.pub('AppStateChange', {
        currentState: appStateRef.current,
        nextState,
        browserState: stateRef.current,
      });
      appStateRef.current = nextState;
    }
  }, []);

  const [debounce] = useDebounce(() => {
    logAppStateChange('passive');
  });

  const logStateChange = useCallback(
    (nextState: State) => {
      const prevState = stateRef.current;
      if (nextState !== prevState) {
        ActivePubSub.pub('StateChange', {
          currentState: stateRef.current,
          nextState,
          appState: appStateRef.current,
        });
        stateRef.current = nextState;
        if (nextState === 'active') {
          logAppStateChange('active');
          debounce(300000);
        }
      }
    },
    [debounce, logAppStateChange],
  );

  function onVisibilityChange(e: Event) {
    logStateChange(getState());
  }

  useEffect(() => {
    return ActivePubSub.sub('Activity', async () => {
      logAppStateChange('active');
      debounce(300000);
    });
  }, [debounce, logAppStateChange]);

  useEventListener('pageshow', onVisibilityChange, window, true);
  useEventListener('focus', onVisibilityChange, document, true);
  useEventListener('blur', onVisibilityChange, document, true);
  useEventListener('visibilitychange', onVisibilityChange, document, true);
  // @ts-ignore freeze
  useEventListener('freeze', onVisibilityChange, window, true);
  // @ts-ignore resume
  useEventListener('resume', onVisibilityChange, window, true);

  return <></>;
}

export default memo(Active);
